import { useMemo } from 'react'
import { ScoopText, ScoopTooltip } from '../../../../ui'
import { severityDistribution } from '../../utils/severityDistribution'
import { CANONICAL_SEVERITIES } from '../../utils/severity'
import styles from './SeverityDistributionBar.module.scss'

const SEGMENT_LABELS = {
    critical: 'Critical',
    severe: 'Severe',
    moderate: 'Moderate',
    watch: 'Watch',
    mild: 'Mild',
    healthy: 'Healthy',
    unprobed: 'Not probed',
}

/**
 * SeverityDistributionBar — the one-glance answer to "how bad is this run?"
 * A stacked proportion bar across the whole scanned scope (severe → healthy,
 * plus a grey never-probed segment), with a colored count legend beneath.
 * Colors come from the canonical severity module; the legend words carry the
 * meaning, so color never rides alone.
 */
export const SeverityDistributionBar = ({ entities = [], entitiesNoun = 'entities', className }) => {
    const counts = useMemo(() => severityDistribution(entities), [entities])
    if (counts.total === 0) return null

    const segments = [...CANONICAL_SEVERITIES, 'unprobed']
        .map((key) => ({ key, count: counts[key], label: SEGMENT_LABELS[key] }))
        .filter((segment) => segment.count > 0)

    // 7% visibility floor WITHOUT distortion (2026-07-29): floor each nonzero
    // segment, then renormalize so the total is exactly 100% — flooring
    // independently overflowed the bar and clipped the rightmost segment.
    const FLOOR = 0.07
    const flooredSum = segments.reduce(
        (sum, segment) => sum + Math.max(segment.count / counts.total, FLOOR),
        0
    )
    const scale = flooredSum > 1 ? 1 / flooredSum : 1
    const widthFor = (segment) =>
        Math.max(segment.count / counts.total, FLOOR) * scale * 100

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <div className={styles.bar} role="img" aria-label={`Severity distribution across ${counts.total} ${entitiesNoun}`}>
                {segments.map((segment) => (
                    <ScoopTooltip
                        key={segment.key}
                        title={`${segment.label}: ${segment.count} of ${counts.total}`}
                    >
                        <div
                            className={`${styles.segment} ${styles[`segment-${segment.key}`]}`}
                            style={{ width: `${widthFor(segment)}%` }}
                        >
                            <span className={styles.segmentCount}>{segment.count}</span>
                        </div>
                    </ScoopTooltip>
                ))}
            </div>
            <div className={styles.legend}>
                {segments.map((segment, index) => (
                    <ScoopText key={segment.key} className={styles.legendItem}>
                        {index > 0 && <span className={styles.legendSeparator}>·</span>}
                        <span className={`${styles.legendCount} ${styles[`legend-${segment.key}`]}`}>
                            {segment.count}
                        </span>{' '}
                        {segment.label}
                    </ScoopText>
                ))}
            </div>
        </div>
    )
}
